import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import OneSignal from 'onesignal-cordova-plugin';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface OneSignalNotification {
  title: string;
  body: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class OnesignalService {

  // ⚠️ REMPLACEZ PAR VOTRE URL D'API
  private apiUrl = environment.oneSignalApi;
  
  // ⚠️ REMPLACEZ PAR VOTRE ONESIGNAL APP ID
  private oneSignalAppId = environment.oneSignalAppId;
  
  private playerId: string = null;
  
  // Observable pour les notifications reçues
  private notificationSubject = new BehaviorSubject<any>(null);
  public notification$ = this.notificationSubject.asObservable();

  private oneSignalReady$ = new BehaviorSubject<boolean>(false);
  isReady$ = this.oneSignalReady$.asObservable();
  
  // Observable pour les notifications ouvertes
  private notificationOpenedSubject = new BehaviorSubject<any>(null);
  public notificationOpened$ = this.notificationOpenedSubject.asObservable();

  constructor(
    private platform: Platform,
    private http: HttpClient
  ) {}

  /**
   * Initialiser OneSignal avec logs détaillés
   */
  async initOneSignal(): Promise<void> {
    console.log('🔍 OneSignal Init - Platform détectée:', this.platform.platforms());
    console.log('🔍 Is Android:', this.platform.is('android'));
    console.log('🔍 Is iOS:', this.platform.is('ios'));
    
    const isNative = this.platform.is('capacitor') || this.platform.is('cordova');

    if (!isNative) {
      console.log('⚠️ OneSignal fonctionne uniquement dans l\'app native (Capacitor/Cordova)');
      console.log('ℹ️ Mode navigateur/PWA détecté - OneSignal non initialisé');
      return;
    }

    try {
      console.log('🚀 Initialisation de OneSignal...');
      console.log('🔑 App ID:', this.oneSignalAppId);
      
      // Vérifier que l'App ID est défini
      if (!this.oneSignalAppId || this.oneSignalAppId === 'VOTRE_ONESIGNAL_APP_ID') {
        console.error('❌ ERREUR: OneSignal App ID non configuré!');
        console.error('⚠️ Veuillez remplacer VOTRE_ONESIGNAL_APP_ID par votre vrai App ID');
        return;
      }
      
      // Initialiser OneSignal avec le nouvel API
      OneSignal.initialize(this.oneSignalAppId);
      console.log('✅ OneSignal.initialize() appelé');

      // Demander la permission
      console.log('🔔 Demande de permission notifications...');
      const permission = await OneSignal.Notifications.requestPermission(true);
      console.log('🔔 Permission result:', permission);

      // Gérer la réception des notifications (foreground)
      OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
        console.log('📱 ========== NOTIFICATION REÇUE (FOREGROUND) ==========');
        console.log('📱 Event:', event);
        
        // Récupérer la notification via getNotification()
        const notification = event.getNotification();
        console.log('📱 Notification:', notification);
        console.log('📱 Title:', notification.title);
        console.log('📱 Body:', notification.body);
        console.log('📱 Additional Data:', notification.additionalData);
        
        this.notificationSubject.next(notification);
        
        // Afficher la notification
        event.preventDefault();
        notification.display();
        console.log('✅ Notification affichée');
      });

      // Gérer le clic sur les notifications
      OneSignal.Notifications.addEventListener('click', (event) => {
        console.log('👆 ========== NOTIFICATION CLIQUÉE ==========');
        console.log('👆 Event:', event);
        console.log('👆 Notification:', event.notification);
        console.log('👆 Additional Data:', event.notification?.additionalData);
        
        this.notificationOpenedSubject.next(event);
      });

      // Vérifier l'état de souscription
      const isOptedIn = await OneSignal.User.pushSubscription.getOptedInAsync();
      console.log('✅ User opted in:', isOptedIn);

      // Récupérer l'ID du joueur (Subscription ID)
      const deviceState = await OneSignal.User.pushSubscription.getIdAsync();
      if (deviceState) {
        console.log('✅ ========== ONESIGNAL CONFIGURÉ ==========');
        console.log('✅ Subscription ID:', deviceState);
        console.log('✅ Opted In:', isOptedIn);
        this.playerId = deviceState;
        
       
      } else {
        console.log('⚠️ Aucun Subscription ID reçu');
        console.log('⚠️ Vérifiez:');
        console.log('   1. Les permissions sont acceptées');
        console.log('   2. L\'App ID OneSignal est correct');
        console.log('   3. Google Play Services (Android) ou APNs (iOS) fonctionnent');
      }

      // Écouter les changements de subscription
      OneSignal.User.pushSubscription.addEventListener('change', (subscription) => {
        console.log('🔄 Subscription changed:', subscription);
        if (subscription.current.id) {
          this.playerId = subscription.current.id;
          console.log('🔄 Nouveau Player ID:', this.playerId);
        
        }
      });

      console.log('✅ OneSignal initialisé avec succès');

    } catch (error) {
      console.error('❌ ========== ERREUR INITIALISATION ONESIGNAL ==========');
      console.error('❌ Error:', error);
      console.error('❌ Vérifiez:');
      console.error('   1. onesignal-cordova-plugin est installé');
      console.error('   2. npx cap sync a été exécuté');
      console.error('   3. L\'app tourne sur un appareil réel');
    }
    this.oneSignalReady$.next(true);
  }

  /**
   * Enregistrer le Player ID sur le serveur
   */
  async registerPlayerIdOnServer(playerId: string, userId: number): Promise<void> {
    
    const platform = this.platform.is('android') ? 'android' : 'ios';

    const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    });

    await this.http.post(
        `${this.apiUrl}/register-onesignal`,
        {
        userId,
        playerId,
        platform
        },
        { headers }
    ).toPromise();

    console.log('✅ PlayerId lié au user:', userId);
    }


  /**
   * Définir un tag utilisateur
   */
  setUserTag(key: string, value: string): void {
    OneSignal.User.addTag(key, value);
    console.log(`✅ Tag défini: ${key} = ${value}`);
  }

  /**
   * Définir plusieurs tags
   */
  setUserTags(tags: { [key: string]: string }): void {
    OneSignal.User.addTags(tags);
    console.log('✅ Tags définis:', tags);
  }

  /**
   * Supprimer un tag
   */
  deleteUserTag(key: string): void {
    OneSignal.User.removeTag(key);
    console.log(`✅ Tag supprimé: ${key}`);
  }

  /**
   * Définir l'email
   */
  setEmail(email: string): void {
    OneSignal.User.addEmail(email);
    console.log(`✅ Email défini: ${email}`);
  }

  /**
   * Définir l'ID externe (votre ID utilisateur)
   */
  setExternalUserId(userId: string): void {
    OneSignal.login(userId);
    console.log(`✅ External User ID défini: ${userId}`);
  }

  /**
   * Obtenir le Player ID actuel
   */
  getPlayerId(): string {
    return this.playerId;
  }

  /**
   * Obtenir le Player ID de manière asynchrone
   */
  async getPlayerIdAsync(): Promise<string | null> {
    try {
      const id = await OneSignal.User.pushSubscription.getIdAsync();
      return id;
    } catch (error) {
      console.error('❌ Erreur récupération Player ID:', error);
      return null;
    }
  }

  /**
   * Demander la permission
   */
  async promptForPushNotifications(): Promise<void> {
    const accepted = await OneSignal.Notifications.requestPermission(true);
    console.log('Permission notifications:', accepted ? 'Acceptée ✅' : 'Refusée ❌');
  }

  /**
   * Désactiver les notifications
   */
  disableNotifications(): void {
    OneSignal.User.pushSubscription.optOut();
    console.log('🔕 Notifications désactivées');
  }

  /**
   * Activer les notifications
   */
  enableNotifications(): void {
    OneSignal.User.pushSubscription.optIn();
    console.log('🔔 Notifications activées');
  }

  /**
   * Vérifier si les notifications sont activées
   */
  async areNotificationsEnabled(): Promise<boolean> {
    try {
      const id = await OneSignal.User.pushSubscription.getIdAsync();
      const optedIn = await OneSignal.User.pushSubscription.getOptedInAsync();
      return id !== null && optedIn;
    } catch (error) {
      console.error('❌ Erreur vérification notifications:', error);
      return false;
    }
  }

  /**
   * Désinscription
   */
  async unregister(): Promise<void> {
    if (!this.playerId) return;

    try {
      await this.http.post(
        `${this.apiUrl}/unregister-onesignal`,
        { playerId: this.playerId }
      ).toPromise();

      // Déconnecter l'utilisateur
      OneSignal.logout();
      
      console.log('✅ Player ID désinscrit');
    } catch (error) {
      console.error('❌ Erreur désinscription:', error);
    }
  }

  /**
   * Envoyer une notification de test
   */
  sendTestNotification(userId: number): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    });

    return this.http.post(
      `${this.apiUrl}/send-test`, 
      { userId },
      { headers }
    );
  }
}